import { Box, Button, Typography } from '@mui/material'
import { DeleteActionButton, EditActionButton } from '../../components/MRTDataGrid/TableActionButtons'
import { MRT_ColumnDef as MRTColumnDef, MRT_Row as MRTRow } from 'material-react-table'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'
import { useConfirmDialogContext } from '../../components/ConfirmDialog'
import {
  useDeleteCustomerDocumentTypeMutation,
  useDeleteTenantDocumentTypeMutation,
  useGetCustomerDocumentTypesQuery,
  useGetTenantDocumentTypesQuery,
} from '../../app/redux-fetch/apiCoreQuery'
import { useGetCustomersQuery } from '../../app/redux-fetch/apiQuery'
import { IDropDownItem } from '../../app/types'
import AddButton from '../../components/Buttons/AddButton'
import ConditionalRender from '../../components/ConditionalRender/ConditionalRender'
import FormDropdown from '../../components/FormDropDown/FormDropdown'
import MRTDataGridv2 from '../../components/MRTDataGrid/MRTDataGridv2'
import YesNoTypography from '../../components/YesNoTypograpphy/YesNoTypography'
import DocumentTypeFormModal from './DocumentTypeFormModal'
import { messages } from './messages'
import { IDocumentType } from './types'

const DocumentTypeTable = () => {
  const { tenantId, customerId, isTenantUser } = useLoggedInUser()
  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [selectedDocumentType, setSelectedDocumentType] = useState<IDocumentType | undefined>(
    undefined
  )
  const { formatMessage } = useIntl()

  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId)

  const {
    data: customerDocTypes = [],
    isLoading: isLoadingCustomerDocTypes,
    refetch: refetchCustomerDocTypes,
  } = useGetCustomerDocumentTypesQuery(
    { customerId: selectedCustomerId as number },
    { skip: !selectedCustomerId }
  )

  const {
    data: tenantDocTypes = [],
    isLoading: isLoadingTenantDocTypes,
    refetch: refetchTenantDocTypes,
  } = useGetTenantDocumentTypesQuery(
    { tenantId },
    { skip: !!selectedCustomerId }
  )

  const documentTypes = selectedCustomerId ? customerDocTypes : tenantDocTypes.filter((dt) => !dt.customerId)
  const isLoadingDocTypes = isLoadingCustomerDocTypes || isLoadingTenantDocTypes
  const refetchDocumentTypes = selectedCustomerId ? refetchCustomerDocTypes : refetchTenantDocTypes

  const { data: customers = [] } = useGetCustomersQuery(tenantId ?? -1, { skip: !tenantId || !isTenantUser })
  const [deleteTenantDocumentType] = useDeleteTenantDocumentTypeMutation()
  const [deleteCustomerDocumentType] = useDeleteCustomerDocumentTypeMutation()
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()
  const { confirm } = useConfirmDialogContext()

  const handleEdit = (row: MRTRow<IDocumentType>) => {
    setSelectedDocumentType(row.original)
    setEditOpen(true)
  }

  const columns = useMemo<MRTColumnDef<IDocumentType>[]>(() => {
    return [
      {
        id: 'name',
        accessorFn: (row) => {
          return <Typography sx={{ fontWeight: 700 }}>{row.name}</Typography>
        },
        header: 'Name',
      },
      {
        id: 'customerName',
        accessorFn: (row) => {
          return row.customer?.name ? (
            <Typography>{row.customer.name}</Typography>
          ) : (
            <Typography color='text.secondary'>N/A</Typography>
          )
        },
        header: 'Customer',
      },
      {
        id: 'categoryName',
        accessorFn: (row) => {
          return row.documentCategory?.name ? (
            <Typography>{row.documentCategory.name}</Typography>
          ) : (
            <Typography color='text.secondary'>N/A</Typography>
          )
        },
        header: 'Document Category',
      },
      {
        accessorFn: (row) => {
          return row.enabled ? 'true' : 'false'
        },
        filterVariant: 'checkbox',
        header: 'Enabled',
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
        Cell: ({ cell }: any) => {
          return <YesNoTypography isYes={cell.getValue() === 'true'} />
        },
      },
      {
        accessorFn: (row) => {
          return row.systemDefined ? 'true' : 'false'
        },
        filterVariant: 'checkbox',
        header: formatMessage(messages.systemDefined),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
        Cell: ({ cell }: any) => {
          return <YesNoTypography isYes={cell.getValue() === 'true'} />
        },
      },
      {
        accessorFn: (row) => {
          return row.inUse ? 'true' : 'false'
        },
        filterVariant: 'checkbox',
        header: formatMessage(messages.inUse),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
        Cell: ({ cell }: any) => {
          return <YesNoTypography isYes={cell.getValue() === 'true'} />
        },
      },
    ]
  }, [formatMessage])

  const handleDelete = async (row: MRTRow<IDocumentType>) => {
    const confirmed = await confirm({
      title: formatMessage(messages.deleteDocumentTypeTitle),
      message: formatMessage(messages.deleteDocumentTypeDescription),
      confirmText: formatMessage(messages.deleteBtn),
      cancelText: formatMessage(messages.cancelBtn),
      severity: 'warning',
      confirmButtonColor: 'error',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      const docType = row.original
      const deletePromise = docType.customerId
        ? deleteCustomerDocumentType({ customerId: docType.customerId, id: docType.id as number })
        : deleteTenantDocumentType({ tenantId: tenantId as number, id: docType.id as number })

      deletePromise
        .unwrap()
        .then(() => {
          enqueueSuccess(messages.documentTypeDeleted)
        })
        .catch(() => {
          enqueueFailure(messages.documentTypeNotDeleted)
        })
    }
  }

  const renderRowActions = ({ row }: { row: MRTRow<IDocumentType> }) => {
    return (
      <Box sx={{ display: 'flex' }}>
        <EditActionButton
          onClick={() => handleEdit(row)}
        />
        <ConditionalRender condition={row.original.inUse == false && !row.original.systemDefined}>
          <DeleteActionButton
            onClick={() => handleDelete(row)}
          />
        </ConditionalRender>
      </Box>
    )
  }

  const handleCreateSubmit = () => {
    refetchDocumentTypes()
    setCreateOpen(false)
  }

  const handleEditSubmit = () => {
    refetchDocumentTypes()
    setEditOpen(false)
    setSelectedDocumentType(undefined)
  }

  return (
    <Box sx={{ pt: '16px' }}>
      {' '}
      {createOpen && (
        <DocumentTypeFormModal
          isOpen={createOpen}
          handleCancel={function (): void {
            setCreateOpen((pv) => !pv)
          }}
          handleSubmit={handleCreateSubmit}
        />
      )}
      {editOpen && selectedDocumentType && (
        <DocumentTypeFormModal
          isOpen={editOpen}
          documentTypeToEdit={selectedDocumentType}
          handleCancel={function (): void {
            setEditOpen((pv) => !pv)
            setSelectedDocumentType(undefined)
          }}
          handleSubmit={handleEditSubmit}
        />
      )}
      <Box>
        <MRTDataGridv2
          initialState={{
            pagination: { pageSize: 100, pageIndex: 0 },
            sorting: [
              {
                id: 'name',
                desc: false,
              },
            ],
          }}
          state={{ isLoading: isLoadingDocTypes }}
          leftHeadingComponent={
            <ConditionalRender condition={isTenantUser}>
              <Box>
                <Box display="flex" justifyContent="flex-end" alignItems="center">
                  <Button
                    size='small'
                    onClick={() => {
                      setSelectedCustomerId(undefined)
                    }}
                    sx={{ textTransform: 'none', p: 0 }}
                  >
                    {formatMessage(messages.clearSelection)}
                  </Button>
                </Box>
                <FormDropdown
                  id='customerId'
                  items={customers as IDropDownItem[]}
                  onChange={(e: any, name: any, newValue: any) => {
                    setSelectedCustomerId && setSelectedCustomerId(newValue)
                  }}
                  sx={{ width: '400px', mt: '12px' }}
                  value={selectedCustomerId}
                />
              </Box>
            </ConditionalRender>
          }
          rightHeadingComponent={<AddButton text={'New'} onClick={() => setCreateOpen(true)} />}
          columns={columns}
          data={documentTypes}
          enableRowActions
          positionActionsColumn='last'
          renderRowActions={renderRowActions}
          muiTableBodyRowProps={{
            sx: {
              cursor: 'pointer',
            },
          }}
        />
      </Box>
    </Box>
  )
}

export default DocumentTypeTable
